// =========================================
// auth.local.js — autenticazione locale (mock, no backend)
// =========================================
//
// Riproduce la superficie di Firebase Auth usata dalle pagine (register, login,
// forgot-password, verify-email) ma con persistenza in localStorage.
//
// NOTA: essendo una demo 100% client-side, le password sono salvate in chiaro
// e la "verifica email" è simulata (nessuna email reale viene inviata). Il
// router auth.js seleziona questo backend quando Firebase non è configurato.

import { TRIAL_MS, trialEndsMillis } from '../data/trial';

const USERS_KEY   = 'iotilize_at_users_v1';
const SESSION_KEY = 'iotilize_at_session_v1';

const hasWindow = () => typeof window !== 'undefined';
const listeners = new Set();

function loadUsers() {
  if (!hasWindow()) return {};
  try { return JSON.parse(window.localStorage.getItem(USERS_KEY)) || {}; } catch { return {}; }
}
function saveUsers(users) {
  if (hasWindow()) window.localStorage.setItem(USERS_KEY, JSON.stringify(users));
}
function getSessionEmail() {
  if (!hasWindow()) return null;
  return window.localStorage.getItem(SESSION_KEY);
}
function setSessionEmail(email) {
  if (!hasWindow()) return;
  if (email) window.localStorage.setItem(SESSION_KEY, email);
  else window.localStorage.removeItem(SESSION_KEY);
}

const slugify = (s) => String(s).toLowerCase().trim()
  .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '').replace(/-+/g, '-').replace(/^-|-$/g, '');

// Errore con `.code` compatibile con la gestione delle pagine auth.
function authError(code, message) {
  const e = new Error(message || code);
  e.code = code;
  return e;
}

function emit() {
  const u = getCurrentUser();
  listeners.forEach(fn => { try { fn(u); } catch { /* ignore */ } });
}

// ── API pubblica ──────────────────────────────────────────────────────────────

export function getCurrentUser() {
  const email = getSessionEmail();
  if (!email) return null;
  const u = loadUsers()[email];
  return u ? publicUser(u) : null;
}

function publicUser(u) {
  return {
    uid: u.uid,
    email: u.email,
    displayName: u.displayName || '',
    nome: u.nome || '',
    cognome: u.cognome || '',
    phone: u.phone || '',
    emailVerified: !!u.emailVerified,
    tenantId: u.tenantId || null,
    tenantName: u.tenantName || '',
    role: u.role || 'titolare',
    taxInfo: u.taxInfo || {},
    notificationPrefs: u.notificationPrefs || {},
    trialEndsAt: trialEndsMillis(u.trialEndsAt),
  };
}

export function onAuthChange(fn) {
  listeners.add(fn);
  // Notifica iniziale asincrona per allineare i consumer.
  if (hasWindow()) Promise.resolve().then(() => fn(getCurrentUser()));
  return () => listeners.delete(fn);
}

// Il backend locale è sincrono: lo stato è già noto, nessuna risoluzione pendente.
export function authPending() { return false; }

export async function registerUser({ email, password, displayName }) {
  const users = loadUsers();
  const key = email.trim().toLowerCase();
  if (users[key]) throw authError('auth/email-already-in-use', 'Questa email è già registrata.');
  if (!password || password.length < 8) throw authError('auth/weak-password', 'Password troppo debole.');
  const uid = 'u_' + Math.random().toString(36).slice(2, 11);
  users[key] = { uid, email: key, password, displayName: displayName || '', emailVerified: false, tenantId: null, tenantName: '', trialEndsAt: Date.now() + TRIAL_MS };
  saveUsers(users);
  setSessionEmail(key);
  emit();
  return publicUser(users[key]);
}

export async function loginUser({ email, password, remember = true }) {
  const users = loadUsers();
  const key = email.trim().toLowerCase();
  const u = users[key];
  if (!u) throw authError('auth/user-not-found', 'Nessun account trovato con questa email.');
  if (u.password !== password) throw authError('auth/invalid-credential', 'Email o password non corretti.');
  setSessionEmail(key);
  emit();
  return publicUser(u);
}

export async function logoutUser() {
  setSessionEmail(null);
  emit();
}

// Simula l'invio email di verifica (no-op realistico).
export async function sendVerificationEmail() {
  const u = getCurrentUser();
  if (!u) throw authError('auth/no-current-user', 'Nessun utente attivo.');
  return true;
}

// In locale la verifica è simulata: marca l'utente come verificato.
export async function markEmailVerified() {
  const email = getSessionEmail();
  if (!email) throw authError('auth/no-current-user', 'Nessun utente attivo.');
  const users = loadUsers();
  if (users[email]) { users[email].emailVerified = true; saveUsers(users); }
  emit();
  return true;
}

export async function sendPasswordReset(email) {
  const users = loadUsers();
  const key = email.trim().toLowerCase();
  if (!users[key]) throw authError('auth/user-not-found', 'Nessun account trovato con questa email.');
  // No-op: nessuna email reale. La promise risolve per coerenza UX.
  return true;
}

// Associa un tenant all'utente corrente (chiamato a registrazione completata).
export function attachTenantToCurrentUser(tenantId, tenantName) {
  const email = getSessionEmail();
  if (!email) return;
  const users = loadUsers();
  if (users[email]) {
    users[email].tenantId = tenantId;
    users[email].tenantName = tenantName;
    saveUsers(users);
  }
  emit();
}

export function updateCurrentUserProfile(patch = {}) {
  const email = getSessionEmail();
  if (!email) return;
  const users = loadUsers();
  const u = users[email];
  if (!u) return;
  const FIELDS = ['displayName', 'nome', 'cognome', 'phone', 'taxInfo', 'notificationPrefs'];
  for (const k of FIELDS) if (patch[k] != null) u[k] = patch[k];
  saveUsers(users);
  emit();
}

// Elimina completamente l'account corrente (equivalente locale di deleteAccount).
export function deleteCurrentAccount() {
  const email = getSessionEmail();
  if (!email) return;
  const users = loadUsers();
  delete users[email];
  saveUsers(users);
  setSessionEmail(null);
  emit();
}

export { slugify };
